import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(Array.from(raw).map((c) => c.charCodeAt(0)));
}

export async function getOrCreatePushSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (!vapidKey) return null;

  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
  });
}

/** Best-effort — persists the chosen UI language server-side so it survives across
 * devices and is available to server-generated content with no live request
 * context (the daily digest, and GET-triggered AI summary/proposals). Failure is
 * silently swallowed since the language switch itself must never be blocked by it. */
export function persistPreferredLanguage(lang: string): void {
  apiFetch('/notifications/preferences', {
    method: 'POST',
    body: JSON.stringify({ lang }),
  }).catch(() => {});
}

export function useSubscribePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('Permission denied');

      const sub = await getOrCreatePushSubscription();
      if (!sub) throw new Error('Push not supported');

      const json = sub.toJSON();
      await apiFetch('/notifications/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          endpoint: json.endpoint,
          p256dh: json.keys?.p256dh ?? '',
          auth: json.keys?.auth ?? '',
        }),
      });
      return sub;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-subscription'] }),
  });
}

export function useUnsubscribePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!('serviceWorker' in navigator)) return;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await apiFetch('/notifications/unsubscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-subscription'] }),
  });
}
